import React from "react";
import { useQuery } from "@tanstack/react-query";
import { AssistantCoachServices, PlayersServices } from "@/services/bedrock";
import { useSocialCoachSession } from "@/utils";

export type OverviewScope = {
  playerId: number | null;
  segmentationDetailId: number | null;
};

/**
 * Scoped assistants can't see the company-wide snapshot, so the overview defaults to the
 * first region they can access, then the first branch, then the first player.
 */
export function useScopedAssistantAccess() {
  const { isAssistantCoach, userId } = useSocialCoachSession();

  const { data: assistant, isPending: isAssistantPending } = useQuery({
    queryKey: ["overview-assistant-scope", userId],
    queryFn: () => AssistantCoachServices.getAssistantCoach(userId!, { extraFields: ["regions", "branches"] }),
    enabled: isAssistantCoach && userId != null,
  });

  const isScoped = isAssistantCoach && !!assistant?.isScoped;
  const firstSegmentationId = assistant?.regions?.[0]?.id ?? assistant?.branches?.[0]?.id ?? null;

  const { data: firstPlayerPage, isPending: isFirstPlayerPending } = useQuery({
    queryKey: ["overview-assistant-scope-first-player", userId],
    queryFn: () => PlayersServices.getPlayers({ page: 1, perPage: 1 }),
    enabled: isScoped && firstSegmentationId == null,
  });

  const allowedSegmentationIds = React.useMemo(() => {
    if (!isScoped) return null;
    return new Set([...(assistant?.regions ?? []), ...(assistant?.branches ?? [])].map(detail => detail.id));
  }, [isScoped, assistant?.regions, assistant?.branches]);

  const isResolving =
    isAssistantCoach && (isAssistantPending || (isScoped && firstSegmentationId == null && isFirstPlayerPending));

  let defaultScope: OverviewScope | null = null;
  if (isScoped) {
    defaultScope =
      firstSegmentationId != null
        ? { playerId: null, segmentationDetailId: firstSegmentationId }
        : { playerId: firstPlayerPage?.content[0]?.id ?? null, segmentationDetailId: null };
  }

  return { isScoped, isResolving, defaultScope, allowedSegmentationIds };
}
